import React, { useEffect, useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import styles from '../components/bodywon.module.css';
import { fetchOrderById } from '../utils/orderApi';
import { fetchOrderItemsForOrder } from '../utils/orderItemApi';
import { ensureLoggedInUserId, isCartUserLoggedIn } from '../utils/cartApi';
import { useSettings } from '../context/SettingsContext';
import { formatOrderDate, formatOrderMoney, getOrderId } from '../admin/order/orderUtils';
import { enrichOrderItems } from './orderHistoryUtils';

const OrderHistoryDetailPage = ({ onRequireLogin }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatPrice } = useSettings();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!isCartUserLoggedIn()) {
      onRequireLogin?.();
      navigate('/', { replace: true });
      return;
    }
    if (!id) return;

    let active = true;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const userId = await ensureLoggedInUserId();
        const [orderData, itemResult] = await Promise.all([
          fetchOrderById(id, true),
          fetchOrderItemsForOrder(id),
        ]);
        if (!active) return;

        const orderUserId = orderData?.UserId ?? orderData?.userId;
        if (orderUserId && userId && Number(orderUserId) !== Number(userId)) {
          setError('Order not found.');
          return;
        }

        setOrder(orderData);
        const enrichedItems = await enrichOrderItems(itemResult.items ?? []);
        setItems(enrichedItems);
      } catch (err) {
        if (active) setError(err.message || 'Could not load order.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [id, navigate, onRequireLogin]);

  if (!isCartUserLoggedIn()) return null;

  const orderId = getOrderId(order) ?? id;

  return (
    <section className={styles.checkoutAddressPage}>
      <Container>
        <div className={styles.checkoutAddressHeader}>
          <h1 className={styles.checkoutAddressTitle}>Order Details</h1>
          <p className={styles.checkoutAddressSub}>
            {order?.OrderNumber ?? order?.orderNumber ?? `Order #${orderId}`}
          </p>
        </div>

        {loading ? (
          <div className={styles.checkoutAddressCard}>Loading order...</div>
        ) : error ? (
          <div className={styles.checkoutAddressCard}>
            <div className={styles.checkoutAddressError}>{error}</div>
            <Button variant="dark" className="mt-3" onClick={() => navigate('/account/orders')}>
              Back to Order History
            </Button>
          </div>
        ) : order ? (
          <div className={styles.checkoutAddressCard}>
            <div className={styles.orderHistoryDetailMeta}>
              <p><strong>Status:</strong> {order.OrderStatus ?? order.orderStatus ?? 'Ordered'}</p>
              <p><strong>Payment:</strong> {order.PaymentStatus ?? order.paymentStatus ?? 'Pending'}</p>
              <p><strong>Ordered At:</strong> {formatOrderDate(order.OrderedAt ?? order.orderedAt)}</p>
              <p><strong>Total:</strong> {formatOrderMoney(order.TotalAmount ?? order.totalAmount)}</p>
            </div>

            <h2 className={styles.orderSectionTitle}>Products Ordered</h2>
            <div className={styles.orderItemsList}>
              {items.length === 0 ? (
                <p className={styles.orderHistoryEmptyText}>No items found for this order.</p>
              ) : items.map((item) => (
                <div key={item.orderItemId ?? item.productId} className={styles.orderItemRow}>
                  <div className={styles.orderItemImage}>
                    {item.imageSrc ? (
                      <img src={item.imageSrc} alt={item.productName} />
                    ) : null}
                  </div>
                  <div className={styles.orderItemInfo}>
                    <p className={styles.orderItemName}>{item.productName}</p>
                    <p className={styles.orderItemMeta}>
                      Qty: {item.quantity} × {formatPrice(item.unitPrice)}
                      {item.sku ? ` · SKU: ${item.sku}` : ''}
                    </p>
                  </div>
                  <div className={styles.orderItemTotal}>
                    {formatPrice(item.totalPrice)}
                  </div>
                </div>
              ))}
            </div>

            <div className={styles.checkoutAddressListActions}>
              <Button variant="outline-secondary" onClick={() => navigate('/account/orders')}>
                Back to Order History
              </Button>
            </div>
          </div>
        ) : null}
      </Container>
    </section>
  );
};

export default OrderHistoryDetailPage;
